import React, { useState } from "react";
import api from "../lib/api";
import { X, Flame, Rocket } from "lucide-react";
import { toast } from "sonner";

const TIERS = [
    { amount: 500, label: "Sortie", desc: "Local burst · 30 days", icon: "·" },
    { amount: 1000, label: "Delivery", desc: "Concentrated push · 30 days", icon: "··" },
    { amount: 5000, label: "Salvo", desc: "Saturation campaign · 30 days", icon: "···" },
];

export default function FuelModal({ territories, onClose }) {
    const [amount, setAmount] = useState(1000);
    const [stateCode, setStateCode] = useState(territories?.[0] || "");
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!stateCode) {
            toast.error("Pick a site to fuel.");
            return;
        }
        setBusy(true);
        try {
            const { data } = await api.post("/payments/fuel-checkout", {
                state_code: stateCode,
                amount,
                origin_url: window.location.origin,
            });
            window.location.href = data.url;
        } catch (ex) {
            toast.error(ex.response?.data?.detail || "Checkout failed");
            setBusy(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={onClose}
            data-testid="fuel-modal"
        >
            <form
                onSubmit={submit}
                onClick={(e) => e.stopPropagation()}
                className="glass rounded-2xl p-7 max-w-md w-full"
            >
                <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                        <div className="w-9 h-9 rounded-md bg-[hsl(40_61%_22%)] flex items-center justify-center text-[hsl(45_85%_68%)]">
                            <Flame size={18} />
                        </div>
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.3em] text-[hsl(45_85%_68%)]">Fuel Ingress</div>
                            <div className="font-heading text-2xl">Fuel a vessel</div>
                        </div>
                    </div>
                    <button type="button" onClick={onClose} className="text-muted-foreground hover:text-foreground" data-testid="fuel-close">
                        <X size={18} />
                    </button>
                </div>

                <p className="text-xs text-muted-foreground mt-3 leading-relaxed">
                    Deploy ad-capital to one of your operating sites. Lands in the Foundry Ad Treasury,
                    auto-credits your monthly Fuel meter, and triggers a 30-day marketing strike on the radar.
                </p>

                {(!territories || territories.length === 0) ? (
                    <div className="mt-6 p-4 rounded-md border border-dashed border-[hsl(40_40%_30%/0.3)] text-sm text-muted-foreground">
                        Claim a territory first — fuel can only be allocated to a site you operate.
                    </div>
                ) : (
                    <>
                        <div className="mt-6">
                            <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">Target vessel</div>
                            <div className="flex flex-wrap gap-2">
                                {territories.map((t) => (
                                    <button
                                        key={t}
                                        type="button"
                                        onClick={() => setStateCode(t)}
                                        data-testid={`fuel-target-${t}`}
                                        className={`px-3 py-1.5 rounded-full text-xs font-mono transition-colors ${
                                            stateCode === t
                                                ? "bg-[hsl(40_61%_53%)] text-[hsl(40_20%_10%)] font-semibold"
                                                : "bg-[hsl(151_55%_13%)] text-muted-foreground hover:text-foreground"
                                        }`}
                                    >
                                        {t}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="mt-5">
                            <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">Payload</div>
                            <div className="grid grid-cols-3 gap-2">
                                {TIERS.map((tier) => (
                                    <button
                                        key={tier.amount}
                                        type="button"
                                        onClick={() => setAmount(tier.amount)}
                                        data-testid={`fuel-tier-${tier.amount}`}
                                        className={`p-3 rounded-lg text-left transition-all ${
                                            amount === tier.amount
                                                ? "bg-[hsl(40_61%_25%/0.6)] ring-2 ring-[hsl(40_61%_53%)] -translate-y-0.5"
                                                : "bg-[hsl(151_55%_13%)] hover:bg-[hsl(151_55%_16%)]"
                                        }`}
                                    >
                                        <div className="text-[10px] uppercase tracking-wider text-[hsl(45_85%_68%)]">{tier.icon} {tier.label}</div>
                                        <div className="font-heading text-xl mt-1">${tier.amount.toLocaleString()}</div>
                                        <div className="text-[10px] text-muted-foreground mt-0.5 leading-tight">{tier.desc}</div>
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="mt-6 p-4 rounded-lg bg-[hsl(151_55%_9%)] border border-[hsl(40_40%_25%/0.18)] flex items-center justify-between">
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Due now</div>
                                <div className="font-heading text-3xl gold-gradient-text">${amount.toLocaleString()}</div>
                            </div>
                            <div className="text-right">
                                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Vessel</div>
                                <div className="font-mono text-lg">{stateCode || "—"}</div>
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={busy || !stateCode}
                            data-testid="fuel-submit"
                            className="w-full mt-5 py-3 rounded-md bg-[hsl(40_61%_53%)] text-[hsl(40_20%_10%)] font-semibold hover:bg-[hsl(45_75%_62%)] transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2"
                        >
                            <Rocket size={16} />
                            {busy ? "Igniting..." : "Ignite payload"}
                        </button>
                    </>
                )}
            </form>
        </div>
    );
}
